import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Paper,
  Stack,
  Avatar,
  Chip,
  IconButton,
  TextField,
  Button,
  Divider,
  Alert,
  Menu,
  MenuItem,
} from '@mui/material'
import {
  ThumbUp as ThumbUpIcon,
  ThumbUpOutlined as ThumbUpOutlinedIcon,
  Reply as ReplyIcon,
  MoreVert as MoreVertIcon,
  Send as SendIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../contexts/AuthContext'
import CommunityLayout from '../../components/community/CommunityLayout'

function PostDetail() {
  const { postId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [post, setPost] = useState(null)
  const [comments, setComments] = useState([])
  const [newComment, setNewComment] = useState('')
  const [loading, setLoading] = useState(true)
  const [reacted, setReacted] = useState(false)
  const [reactionCount, setReactionCount] = useState(0)
  const [anchorEl, setAnchorEl] = useState(null)
  const [profile, setProfile] = useState(null)

  useEffect(() => {
    fetchPost()
    fetchComments()
    if (user) {
      checkReaction()
      fetchProfile()
    }
    incrementViewCount()

    // Realtime subscriptions
    const commentsChannel = supabase
      .channel(`comments-${postId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'comments', filter: `post_id=eq.${postId}` },
        handleCommentChange
      )
      .subscribe()

    const reactionsChannel = supabase
      .channel(`reactions-${postId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'reactions', filter: `post_id=eq.${postId}` },
        fetchReactionCount
      )
      .subscribe()

    return () => {
      supabase.removeChannel(commentsChannel)
      supabase.removeChannel(reactionsChannel)
    }
  }, [postId, user])

  const fetchPost = async () => {
    try {
      const { data: postData, error } = await supabase
        .from('posts')
        .select('*')
        .eq('id', postId)
        .single()

      if (error) throw error

      // Fetch author separately
      const { data: authorData } = await supabase
        .from('profiles')
        .select('id, full_name, avatar_url, role')
        .eq('id', postData.author_id)
        .single()

      setPost({ ...postData, author: authorData })

      // Fetch reaction count
      const { count } = await supabase
        .from('reactions')
        .select('*', { count: 'exact', head: true })
        .eq('post_id', postId)

      setReactionCount(count || 0)
    } catch (error) {
      console.error('Error fetching post:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchComments = async () => {
    try {
      const { data: commentsData, error } = await supabase
        .from('comments')
        .select('*')
        .eq('post_id', postId)
        .eq('is_hidden', false)
        .is('parent_id', null)
        .order('created_at', { ascending: true })

      if (error) throw error

      // Fetch authors separately
      const authorIds = [...new Set(commentsData.map(c => c.author_id))]
      const { data: authorsData } = await supabase
        .from('profiles')
        .select('id, full_name, avatar_url, role')
        .in('id', authorIds)

      const authorsMap = {}
      authorsData?.forEach(author => {
        authorsMap[author.id] = author
      })

      const commentsWithAuthors = commentsData.map(comment => ({
        ...comment,
        author: authorsMap[comment.author_id],
      }))

      setComments(commentsWithAuthors)
    } catch (error) {
      console.error('Error fetching comments:', error)
    }
  }

  const fetchProfile = async () => {
    try {
      const { data } = await supabase.from('profiles').select('role').eq('id', user.id).single()

      setProfile(data)
    } catch (error) {
      console.error('Error fetching profile:', error)
    }
  }

  const checkReaction = async () => {
    try {
      const { data, error } = await supabase
        .from('reactions')
        .select('id')
        .eq('post_id', postId)
        .eq('user_id', user.id)
        .maybeSingle()

      if (error && error.code !== 'PGRST116') throw error
      setReacted(!!data)
    } catch (error) {
      console.error('Error checking reaction:', error)
    }
  }

  const fetchReactionCount = async () => {
    const { count } = await supabase
      .from('reactions')
      .select('*', { count: 'exact', head: true })
      .eq('post_id', postId)

    setReactionCount(count || 0)
  }

  const incrementViewCount = async () => {
    await supabase
      .from('posts')
      .update({ view_count: (post?.view_count || 0) + 1 })
      .eq('id', postId)
  }

  const handleCommentChange = payload => {
    if (payload.eventType === 'INSERT') {
      fetchComments()
    } else if (payload.eventType === 'UPDATE') {
      setComments(prev => prev.map(c => (c.id === payload.new.id ? { ...c, ...payload.new } : c)))
    } else if (payload.eventType === 'DELETE') {
      setComments(prev => prev.filter(c => c.id !== payload.old.id))
    }
  }

  const handleReaction = async () => {
    if (!user) {
      alert('Please sign in to react')
      return
    }

    try {
      if (reacted) {
        await supabase.from('reactions').delete().eq('post_id', postId).eq('user_id', user.id)

        setReacted(false)
      } else {
        await supabase
          .from('reactions')
          .insert([{ post_id: postId, user_id: user.id, type: 'like' }])

        setReacted(true)
      }
    } catch (error) {
      console.error('Error toggling reaction:', error)
    }
  }

  const handleCommentSubmit = async e => {
    e.preventDefault()
    if (!newComment.trim()) return

    try {
      await supabase.from('comments').insert([
        {
          post_id: postId,
          body: newComment.trim(),
          author_id: user.id,
        },
      ])

      setNewComment('')
    } catch (error) {
      console.error('Error creating comment:', error)
    }
  }

  const handleDeleteComment = async (commentId) => {
  if (window.confirm('Are you sure you want to delete this comment?')) {
    try {
      const { error } = await supabase
        .from('comments')
        .delete()
        .eq('id', commentId)

      if (error) throw error

      setComments(prev => prev.filter(c => c.id !== commentId))
    } catch (error) {
      console.error('Error deleting comment:', error)
      alert('Failed to delete comment')
    }
  }
}

const handleHideComment = async (commentId) => {
  try {
    const { error } = await supabase
      .from('comments')
      .update({ is_hidden: true })
      .eq('id', commentId)

    if (error) throw error

    setComments(prev => prev.filter(c => c.id !== commentId))
  } catch (error) {
    console.error('Error hiding comment:', error)
    alert('Failed to hide comment')
  }
}

  const handleDeletePost = async () => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await supabase.from('posts').delete().eq('id', postId)
        navigate('/community')
      } catch (error) {
        console.error('Error deleting post:', error)
      }
    }
    setAnchorEl(null)
  }

  const handleHidePost = async () => {
    try {
      await supabase.from('posts').update({ is_hidden: true }).eq('id', postId)
      navigate('/community')
    } catch (error) {
      console.error('Error hiding post:', error)
    }
    setAnchorEl(null)
  }

  const formatDate = dateString => {
    const date = new Date(dateString)
    return date.toLocaleString()
  }

  const canModerate = profile?.role === 'admin' || profile?.role === 'moderator'
  const isAuthor = user?.id === post?.author_id

  if (loading)
    return (
      <CommunityLayout>
        <Typography>Loading...</Typography>
      </CommunityLayout>
    )
  if (!post)
    return (
      <CommunityLayout>
        <Alert severity="error">Post not found</Alert>
      </CommunityLayout>
    )

  return (
    <CommunityLayout>
      <Paper elevation={0} sx={{ p: 4, border: 1, borderColor: 'divider', mb: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              src={post.author?.avatar_url}
              sx={{
                width: 48,
                height: 48,
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              }}
            >
              {post.author?.full_name?.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body1" fontWeight={600}>
                  {post.author?.full_name}
                </Typography>
                {post.author?.role !== 'member' && (
                  <Chip
                    label={post.author?.role}
                    size="small"
                    color={post.author?.role === 'admin' ? 'error' : 'warning'}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {formatDate(post.created_at)}
              </Typography>
            </Box>
          </Stack>

          {(isAuthor || canModerate) && (
            <>
              <IconButton onClick={e => setAnchorEl(e.currentTarget)}>
                <MoreVertIcon />
              </IconButton>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                {isAuthor && <MenuItem onClick={handleDeletePost}>Delete Post</MenuItem>}
                {canModerate && <MenuItem onClick={handleHidePost}>Hide Post</MenuItem>}
              </Menu>
            </>
          )}
        </Stack>

        <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
          {post.title}
        </Typography>

        <Typography variant="body1" sx={{ mb: 3, whiteSpace: 'pre-wrap' }}>
          {post.body}
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center">
          <IconButton onClick={handleReaction} color={reacted ? 'primary' : 'default'}>
            {reacted ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
          </IconButton>
          <Typography variant="body2">{reactionCount}</Typography>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: 2 }}>
            <ViewIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {post.view_count} views
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      <Typography variant="h6" fontWeight={600} sx={{ mb: 2, color: 'text.primary' }}>
        Comments ({comments.length})
      </Typography>

      {user && (
        <Paper elevation={0} sx={{ p: 3, mb: 3, border: 1, borderColor: 'divider' }}>
          <form onSubmit={handleCommentSubmit}>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Write a comment..."
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Button
              type="submit"
              variant="contained"
              startIcon={<SendIcon />}
              disabled={!newComment.trim()}
              sx={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                textTransform: 'none',
              }}
            >
              Post Comment
            </Button>
          </form>
        </Paper>
      )}

      <Stack spacing={2}>
        {comments.map(comment => (
          <Paper key={comment.id} elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={2}>
              <Avatar
                src={comment.author?.avatar_url}
                sx={{
                  width: 36,
                  height: 36,
                  background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                }}
              >
                {comment.author?.full_name?.charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {comment.author?.full_name}
                  </Typography>
                  {comment.author?.role !== 'member' && (
                    <Chip
                      label={comment.author?.role}
                      size="small"
                      color={comment.author?.role === 'admin' ? 'error' : 'warning'}
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                  )}
                  <Typography variant="caption" color="text.secondary">
                    · {formatDate(comment.created_at)}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {comment.body}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </CommunityLayout>
  )
}

export default PostDetail
